import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, IconButton, Dialog, DialogContent, Menu, MenuItem, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

import {
  GalleryImage, reactToGalleryImage, removeGalleryReaction,
} from '../../services/gallery_api';
import ActivityReactions from '../common/ActivityReactions';
import { formatRelativeTime } from '../../utils/dateUtils';
import { useUser } from '../../contexts/UserContext';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

interface Props {
  images: GalleryImage[];
  /** 現在表示中のインデックス。null でクローズ。 */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  isPublic?: boolean;
  /** owner用。指定時、右上の ⋮ メニューに「編集」を出す（スマホでホバー導線が無い問題の解消）。 */
  onEdit?: (image: GalleryImage) => void;
  /** owner用。指定時、右上の ⋮ メニューに「削除」を出す。破壊操作なので×の隣に直置きしない。 */
  onDelete?: (image: GalleryImage) => void;
  /** リアクション変更を親へ通知（一覧キャッシュ同期用） */
  onReactionUpdate?: (
    imageId: number,
    reactions: { [key: number]: number },
    userReaction: number | null,
  ) => void;
}

/**
 * ギャラリー画像のライトボックス（Dialog）。
 * ギャラリータブ・アクティビティフィード双方から共通利用する。
 * リアクションは GalleryReaction（画像が真実の源）に一本化。
 */
const GalleryLightbox: React.FC<Props> = ({
  images, index, onClose, onNavigate, isPublic = false, onEdit, onDelete, onReactionUpdate,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const touchStartX = useRef<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const hasOwnerMenu = !!onEdit || !!onDelete;

  const previewImage = index != null ? images[index] : null;
  const hasPrev = index != null && index > 0;
  const hasNext = index != null && index < images.length - 1;
  const goPrev = () => { if (index != null && index > 0) onNavigate(index - 1); };
  const goNext = () => { if (index != null && index < images.length - 1) onNavigate(index + 1); };

  // ネタバレのクッション: 直接開いた画像は表示、スライドで到達したネタバレはぼかす（オーナーは対象外）
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const prevIndexRef = useRef<number | null>(null);
  useEffect(() => {
    if (index == null) {
      prevIndexRef.current = null;
      setRevealed((prev) => (prev.size ? new Set() : prev));
      return;
    }
    if (prevIndexRef.current == null) {
      const img = images[index];
      if (img) setRevealed(new Set([img.id]));  // 直接タップして開いた画像は解除
    }
    prevIndexRef.current = index;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const spoilerGated = !!previewImage && previewImage.is_spoiler && isPublic && !revealed.has(previewImage.id);
  const revealCurrent = () => {
    if (previewImage) setRevealed((prev) => new Set(prev).add(previewImage.id));
  };

  useEffect(() => {
    if (index == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, images]);

  return (
    <Dialog
      open={previewImage != null}
      onClose={onClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(15,15,19,0.98)',
          borderRadius: isMobile ? 0 : '12px',
          ...(isMobile ? {} : { m: 2 }),
        },
      }}
    >
      {previewImage && (
        <>
          <Box sx={{
            position: 'absolute', top: 8, right: 8, zIndex: 10,
            display: 'flex', gap: 0.5,
          }}>
            {hasOwnerMenu && (
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                title="メニュー"
                sx={{
                  color: '#fff', backgroundColor: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(4px)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                }}
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {previewImage.image_url && (
              <IconButton
                href={previewImage.image_url}
                target="_blank"
                rel="noopener noreferrer"
                title="原寸を別タブで開く"
                sx={{
                  color: '#fff', backgroundColor: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(4px)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            <IconButton
              onClick={onClose}
              title="閉じる"
              sx={{
                color: '#fff', backgroundColor: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(4px)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* owner用メニュー。削除は破壊操作なので×の隣に直置きせず、ここに畳む（一覧タイルと同じ構造） */}
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            {onEdit && (
              <MenuItem onClick={() => { setMenuAnchor(null); onEdit(previewImage); }}>
                編集
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                onClick={() => { setMenuAnchor(null); onDelete(previewImage); }}
                sx={{ color: 'error.main' }}
              >
                削除
              </MenuItem>
            )}
          </Menu>

          {/* 位置カウンター */}
          {index != null && images.length > 1 && (
            <Box sx={{
              position: 'absolute', top: 12, left: 12, zIndex: 10,
              px: 1, py: 0.25, borderRadius: '999px',
              backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
              color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', lineHeight: 1.6,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {index + 1} / {images.length}
            </Box>
          )}
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {previewImage.image_url && (
              <Box
                sx={{
                  position: 'relative', width: '100%',
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  flexGrow: isMobile ? 1 : 0, minHeight: 0, overflow: 'hidden',
                }}
                onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                  if (touchStartX.current == null) return;
                  const dx = e.changedTouches[0].clientX - touchStartX.current;
                  if (Math.abs(dx) > 50) { dx > 0 ? goPrev() : goNext(); }
                  touchStartX.current = null;
                }}
              >
                <img
                  src={previewImage.image_url}
                  alt={spoilerGated ? '' : (previewImage.caption || '')}
                  onClick={spoilerGated ? revealCurrent : onClose}
                  style={{
                    maxWidth: '100%', maxHeight: isMobile ? '100%' : '78vh', objectFit: 'contain',
                    display: 'block', cursor: spoilerGated ? 'pointer' : 'zoom-out',
                    // ぼかしは即時適用（transitionを付けるとスライド時に一瞬素の画像が見えてしまう）
                    filter: spoilerGated ? 'blur(40px)' : 'none',
                  }}
                />
                {/* スライドで到達したネタバレのクッション */}
                {spoilerGated && (
                  <Box
                    onClick={revealCurrent}
                    sx={{
                      position: 'absolute', inset: 0, zIndex: 4, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                      color: 'rgba(255,255,255,0.9)',
                    }}
                  >
                    <VisibilityOffIcon sx={{ fontSize: 32 }} />
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1.5, py: 0.5, borderRadius: '999px',
                      backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                      fontSize: '0.8rem',
                    }}>
                      ネタバレ · タップで表示
                    </Box>
                  </Box>
                )}
                {!isMobile && hasPrev && (
                  <Box
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    title="前へ"
                    sx={{
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: { sm: 80, md: 110 }, zIndex: 5,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-start', pl: 1.5,
                      cursor: 'pointer', opacity: 0, transition: 'opacity 0.2s ease',
                      background: 'linear-gradient(to right, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Box sx={{
                      width: 38, height: 76, borderRadius: '10px',
                      backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      <ChevronLeftIcon />
                    </Box>
                  </Box>
                )}
                {!isMobile && hasNext && (
                  <Box
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    title="次へ"
                    sx={{
                      position: 'absolute', right: 0, top: 0, bottom: 0, width: { sm: 80, md: 110 }, zIndex: 5,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1.5,
                      cursor: 'pointer', opacity: 0, transition: 'opacity 0.2s ease',
                      background: 'linear-gradient(to left, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Box sx={{
                      width: 38, height: 76, borderRadius: '10px',
                      backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      <ChevronRightIcon />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', maxWidth: 900 }}>
              {previewImage.caption && (
                <Typography
                  variant="body2"
                  sx={{ color: '#fff', whiteSpace: 'pre-wrap', mb: 1.5, lineHeight: 1.6 }}
                >
                  {previewImage.caption}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                  color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem',
                  mb: 1.5,
                  '& > *': { flexShrink: 0 },
                  '& > *:not(:last-child)::after': {
                    content: '"·"', mx: 1, color: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                {previewImage.game && (
                  <Box
                    component="span"
                    onClick={() => navigate(`/game/${previewImage.game!.id}`)}
                    sx={{
                      color: 'rgba(255,255,255,0.75)', cursor: 'pointer',
                      maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      '&:hover': { color: '#fff', textDecoration: 'underline' },
                    }}
                  >
                    {previewImage.game.name}
                  </Box>
                )}
                <Box component="span" title={new Date(previewImage.created_at).toLocaleString('ja-JP')}>
                  {formatRelativeTime(previewImage.created_at)}
                </Box>
                {previewImage.width && previewImage.height && (
                  <Box component="span">{previewImage.width}×{previewImage.height}</Box>
                )}
                {/* 容量は枚数管理につき通常非表示。管理者のみ運用確認用に表示 */}
                {user?.is_superuser && previewImage.size_bytes > 0 && (
                  <Box component="span">{formatBytes(previewImage.size_bytes)}</Box>
                )}
                {previewImage.is_spoiler && (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                    <VisibilityOffIcon sx={{ fontSize: 12 }} />ネタバレ
                  </Box>
                )}
              </Box>

              <ActivityReactions
                targetId={previewImage.id}
                reactions={(previewImage.reaction_counts as { [key: number]: number } | null) || {}}
                userReaction={previewImage.user_reaction}
                isPublic={isPublic}
                addReaction={reactToGalleryImage}
                removeReaction={removeGalleryReaction}
                onReactionUpdate={(_targetId, newReactions, newUserReaction) => {
                  onReactionUpdate?.(previewImage.id, newReactions, newUserReaction);
                }}
              />
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default GalleryLightbox;
